import React, { useState } from "react";
import {
  Box, Flex, HStack, Text, Icon, Collapse, Skeleton, VStack, IconButton,
  Menu, MenuButton, MenuList, MenuItem, Tooltip,
} from "@chakra-ui/react";
import {
  HiChevronRight, HiOutlineEllipsisHorizontal, HiOutlinePencilSquare,
  HiOutlineTrash, HiOutlineArrowUp, HiOutlineArrowDown, HiOutlineDocumentText,
  HiOutlineFilm, HiOutlinePaperClip, HiOutlinePhoto,
} from "react-icons/hi2";
import { c, PALETTES } from "../classroomColors";
import { brand, onTint } from "../standeTokens";
import InlineAdd from "./InlineAdd";
import { countLessons, lessonParts, isEmpty, moduleIsEmpty } from "./contentTokens";

/** The little glyph row under a lesson: what is actually in it. */
const PartGlyphs = ({ segment }) => {
  const { videos, images, files, hasReading } = lessonParts(segment);
  const items = [
    hasReading && { icon: HiOutlineDocumentText, n: null, label: "Reading" },
    videos && { icon: HiOutlineFilm, n: videos, label: "Videos" },
    images && { icon: HiOutlinePhoto, n: images, label: "Images" },
    files && { icon: HiOutlinePaperClip, n: files, label: "Files" },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <HStack spacing="9px" mt="3px">
      {items.map((it, i) => (
        <HStack key={i} spacing="3px" title={it.label}>
          <Icon as={it.icon} boxSize="12px" color={c.textMuted} />
          {it.n ? (
            <Text fontSize="11px" color={c.textMuted} sx={{ fontVariantNumeric: "tabular-nums" }}>
              {it.n}
            </Text>
          ) : null}
        </HStack>
      ))}
    </HStack>
  );
};

const RowMenu = ({ label, isDark, onRename, onDelete, onUp, onDown, canUp, canDown }) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  return (
    <Menu placement="bottom-end" autoSelect={false}>
      <MenuButton
        as={IconButton}
        aria-label={`Options for ${label}`}
        icon={<Icon as={HiOutlineEllipsisHorizontal} boxSize="17px" />}
        variant="ghost"
        size="xs"
        minW="24px"
        h="24px"
        borderRadius="6px"
        color={c.textMuted}
        onClick={(e) => e.stopPropagation()}
        _hover={{ bg: c.hover, color: c.text }}
      />
      <MenuList
        minW="180px" py="5px" bg={p.surface} color={p.text}
        border="1px solid" borderColor={p.border} borderRadius="10px"
        boxShadow="0 12px 30px -12px rgba(0,0,0,0.4)" fontSize="13px"
      >
        <MenuItem icon={<Icon as={HiOutlinePencilSquare} boxSize="15px" />} bg="transparent"
          _hover={{ bg: p.hover }} _focus={{ bg: p.hover }}
          onClick={(e) => { e.stopPropagation(); onRename(); }}>
          Rename
        </MenuItem>
        {canUp && (
          <MenuItem icon={<Icon as={HiOutlineArrowUp} boxSize="15px" />} bg="transparent"
            _hover={{ bg: p.hover }} _focus={{ bg: p.hover }}
            onClick={(e) => { e.stopPropagation(); onUp(); }}>
            Move up
          </MenuItem>
        )}
        {canDown && (
          <MenuItem icon={<Icon as={HiOutlineArrowDown} boxSize="15px" />} bg="transparent"
            _hover={{ bg: p.hover }} _focus={{ bg: p.hover }}
            onClick={(e) => { e.stopPropagation(); onDown(); }}>
            Move down
          </MenuItem>
        )}
        <MenuItem icon={<Icon as={HiOutlineTrash} boxSize="15px" />} bg="transparent"
          color={isDark ? "#ff8a7a" : "#b3261e"}
          _hover={{ bg: p.hover }} _focus={{ bg: p.hover }}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          Delete
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

/**
 * The course outline: modules that open to reveal their lessons, the way a
 * course syllabus reads. A teacher adds and reorders in place; a student just
 * sees what is in each lesson before opening it.
 */
const CourseOutline = ({
  modules,
  selectedGuid,
  onSelect,
  isInstructor,
  isDark,
  onAddModule,
  onAddLesson,
  onRename,
  onDelete,
  onMove,
}) => {
  const [open, setOpen] = useState(() => new Set());
  const b = brand(isDark);
  const tint = onTint(isDark);

  const toggle = (guid) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(guid) ? next.delete(guid) : next.add(guid);
      return next;
    });

  if (modules === null) {
    return (
      <VStack align="stretch" spacing="8px">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} h="58px" borderRadius="12px" startColor={c.surfaceSubtle} endColor={c.hover} />
        ))}
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing="10px">
      {modules.map((mod, mi) => {
        const lessons = mod.child_segments || [];
        const isOpen = open.has(mod.guid);
        const modActive = selectedGuid === mod.guid;
        const stub = isInstructor && moduleIsEmpty(mod);

        return (
          <Box
            key={mod.guid}
            border="1px solid"
            borderColor={modActive ? c.text : c.borderSoft}
            borderRadius="12px"
            overflow="hidden"
            bg={c.surface}
            transition="border-color 0.15s"
          >
            {/* module header */}
            <Flex
              as="button"
              onClick={() => { onSelect(mod); toggle(mod.guid); }}
              width="100%"
              textAlign="left"
              align="center"
              gap="12px"
              px="14px"
              py="13px"
              bg={modActive ? c.hover : "transparent"}
              transition="background 0.15s"
              _hover={{ bg: c.hover }}
            >
              <Icon
                as={HiChevronRight}
                boxSize="15px"
                color={c.textMuted}
                flexShrink={0}
                transform={isOpen ? "rotate(90deg)" : "none"}
                transition="transform 0.18s"
              />

              <Box flex="1" minW={0}>
                <HStack spacing="8px" mb="2px">
                  <Text fontSize="11px" fontWeight="500" color={c.textMuted} letterSpacing="0.04em">
                    MODULE {String(mi + 1).padStart(2, "0")}
                  </Text>
                  {stub && (
                    <Text
                      fontSize="10px" fontWeight="500" px="6px" py="1px" borderRadius="full"
                      bg={b.lightYellow} color={tint.lightYellow}
                      title="Nothing in this module yet"
                    >
                      Empty
                    </Text>
                  )}
                </HStack>
                <Text fontSize="14.5px" fontWeight="500" color={c.text} noOfLines={1}>
                  {mod.name}
                </Text>
              </Box>

              <Text fontSize="12px" color={c.textMuted} flexShrink={0} whiteSpace="nowrap"
                sx={{ fontVariantNumeric: "tabular-nums" }}>
                {countLessons(mod)} {countLessons(mod) === 1 ? "lesson" : "lessons"}
              </Text>

              {isInstructor && (
                <Box onClick={(e) => e.stopPropagation()} flexShrink={0}>
                  <RowMenu
                    label={mod.name}
                    isDark={isDark}
                    onRename={() => onRename(mod)}
                    onDelete={() => onDelete(mod, "module")}
                    onUp={() => onMove(mod, -1, null)}
                    onDown={() => onMove(mod, 1, null)}
                    canUp={mi > 0}
                    canDown={mi < modules.length - 1}
                  />
                </Box>
              )}
            </Flex>

            {/* lessons */}
            <Collapse in={isOpen} animateOpacity>
              <Box borderTop="1px solid" borderColor={c.borderSoft} py="6px">
                {lessons.map((lesson, li) => {
                  const active = selectedGuid === lesson.guid;
                  return (
                    <Flex
                      key={lesson.guid}
                      as="button"
                      onClick={() => onSelect(lesson)}
                      width="100%"
                      textAlign="left"
                      align="flex-start"
                      gap="11px"
                      pl="24px"
                      pr="12px"
                      py="9px"
                      bg={active ? c.hover : "transparent"}
                      transition="background 0.15s"
                      _hover={{ bg: c.hover }}
                      position="relative"
                    >
                      {active && (
                        <Box position="absolute" left="12px" top="10px" bottom="10px" width="2px"
                          borderRadius="2px" bg={b.primaryBlue} />
                      )}
                      <Text fontSize="11.5px" color={c.textMuted} mt="1px" flexShrink={0}
                        sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {mi + 1}.{li + 1}
                      </Text>
                      <Box flex="1" minW={0}>
                        <HStack spacing="7px">
                          <Text fontSize="13.5px" fontWeight={active ? "500" : "400"} color={c.text} noOfLines={1}>
                            {lesson.name}
                          </Text>
                          {isInstructor && isEmpty(lesson) && (
                            <Tooltip label="Nothing in this lesson yet" placement="top" hasArrow fontSize="11px">
                              <Box boxSize="6px" borderRadius="full" bg={b.secondaryOrange} flexShrink={0} />
                            </Tooltip>
                          )}
                        </HStack>
                        <PartGlyphs segment={lesson} />
                      </Box>
                      {isInstructor && (
                        <Box onClick={(e) => e.stopPropagation()} flexShrink={0}>
                          <RowMenu
                            label={lesson.name}
                            isDark={isDark}
                            onRename={() => onRename(lesson)}
                            onDelete={() => onDelete(lesson, "lesson")}
                            onUp={() => onMove(lesson, -1, mod.guid)}
                            onDown={() => onMove(lesson, 1, mod.guid)}
                            canUp={li > 0}
                            canDown={li < lessons.length - 1}
                          />
                        </Box>
                      )}
                    </Flex>
                  );
                })}

                {isInstructor ? (
                  <Box pl="12px" pr="6px" pt={lessons.length ? "2px" : "0"}>
                    <InlineAdd
                      label="Add a lesson"
                      placeholder="Lesson name, then Enter"
                      indent={12}
                      onAdd={(name) => onAddLesson(mod.guid, name)}
                    />
                  </Box>
                ) : lessons.length === 0 ? (
                  <Text fontSize="12.5px" color={c.textMuted} pl="24px" py="8px" fontStyle="italic">
                    No lessons in this module yet.
                  </Text>
                ) : null}
              </Box>
            </Collapse>
          </Box>
        );
      })}

      {isInstructor && (
        <Box
          border="1px dashed"
          borderColor={c.border}
          borderRadius="12px"
          _hover={{ borderColor: c.textMuted }}
          transition="border-color 0.15s"
        >
          <InlineAdd
            label="Add a module"
            placeholder="Module name, then Enter"
            size="md"
            onAdd={onAddModule}
          />
        </Box>
      )}

      {!isInstructor && modules.length === 0 && (
        <Text fontSize="13px" color={c.textSub} py="20px" textAlign="center" fontWeight="300">
          Your teacher hasn't added any course content yet.
        </Text>
      )}
    </VStack>
  );
};

export default CourseOutline;
